"use client";

import Link from "next/link";
import { useEffect, useState } from "react";
import { usePrivy } from "@privy-io/react-auth";
import PortalNavBar from "../components/PortalNavBar";
import { backendFetch, getCurrentUser } from "../lib/backend";

export const dynamic = "force-dynamic";

const API_BASE_URL = process.env.NEXT_PUBLIC_API_URL ?? "http://localhost:3000";

export default function PseEnterprisePartnersPage() {
  const { ready, authenticated, login, getAccessToken } = usePrivy();
  const [copied, setCopied] = useState(false);

  // PSE Auto-detected Agreement & Wallet State
  const [agreementAddress, setAgreementAddress] = useState("");
  const [customerWallet, setCustomerWallet] = useState("");
  const [generatedLink, setGeneratedLink] = useState<string | null>(null);
  const [generatedIframe, setGeneratedIframe] = useState<string | null>(null);
  const [checkoutLinkError, setCheckoutLinkError] = useState<string | null>(null);
  const [creatingCheckoutLink, setCreatingCheckoutLink] = useState(false);

  const navLinks = [
    { href: "/dashboard", label: "Dashboard" },
    { href: "/account", label: "Account" },
    { href: "/accounts-breakdown", label: "Accounts Breakdown" },
    { href: "/agpsira-checkout-app", label: "AGPSIRA Checkout App", active: true },
    { href: "/revenue-history", label: "Revenue History" },
    { href: "/earnings-history", label: "Earnings History" },
    { href: "/sales-history", label: "Sales History" },
    { href: "/distribution-controls", label: "Distribution Controls" },
  ];

  // Load PSE account & agreement info
  useEffect(() => {
    if (!ready || !authenticated) return;

    async function loadPseDetails() {
      try {
        const token = await getAccessToken();
        if (!token) return;

        const account = await getCurrentUser(token);
        if (account?.user?.walletAddress) {
          setCustomerWallet(account.user.walletAddress);
        }

        const memberRes = await backendFetch("/api/participant-access/me", token);
        if (memberRes.ok) {
          const memberData = await memberRes.json();
          if (memberData.memberships?.[0]?.agreementAddress) {
            setAgreementAddress(memberData.memberships[0].agreementAddress);
          }
        }
      } catch (err) {
        console.warn("Notice: could not auto-populate agreement:", err);
      }
    }

    void loadPseDetails();
  }, [authenticated, getAccessToken, ready]);

  // Checkout links are signed by the API; the browser never chooses the wallet in the session.
  useEffect(() => {
    let active = true;

    async function createCheckoutLink() {
      if (!ready || !authenticated || !agreementAddress || !customerWallet) return;

      try {
        setCreatingCheckoutLink(true);
        setCheckoutLinkError(null);
        const accessToken = await getAccessToken();
        if (!accessToken) throw new Error("Sign in again to create a checkout link.");

        const response = await fetch(`${API_BASE_URL}/api/pos/checkout-sessions`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${accessToken}`,
          },
          body: JSON.stringify({ agreementAddress }),
        });
        const data = await response.json();
        if (!response.ok || !data.success) {
          throw new Error(data.error || "Could not create a secure checkout link.");
        }

        if (active) {
          const link = `${window.location.origin}/pos/checkout?token=${encodeURIComponent(data.token)}`;
          setGeneratedLink(link);
          setGeneratedIframe(`<iframe src="${link}" width="100%" height="680" frameborder="0" allow="payment"></iframe>`);
        }
      } catch (error) {
        if (active) {
          setGeneratedLink(null);
          setGeneratedIframe(null);
          setCheckoutLinkError(error instanceof Error ? error.message : "Could not create checkout link.");
        }
      } finally {
        if (active) setCreatingCheckoutLink(false);
      }
    }

    void createCheckoutLink();
    return () => {
      active = false;
    };
  }, [agreementAddress, authenticated, customerWallet, getAccessToken, ready]);

  const handleCopy = (text: string) => {
    if (typeof navigator !== "undefined" && navigator.clipboard) {
      void navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  if (!ready || !authenticated) {
    return (
      <main className="min-h-screen bg-slate-950 px-4 py-12 text-white md:px-8 md:py-16">
        <section className="mx-auto max-w-xl rounded-3xl border border-slate-800 bg-slate-900 p-8 text-center">
          <div className="mb-2 inline-block rounded-full bg-blue-500/20 px-3 py-1 text-xs font-semibold text-blue-300">
            PSE Participant Portal
          </div>
          <h1 className="mt-2 text-3xl font-bold">AGPSIRA Checkout App</h1>
          <p className="mt-3 text-slate-300">
            Sign in to connect your agreement to the AGPSIRA Checkout App and manage your checkout routing.
          </p>
          <button
            type="button"
            onClick={login}
            className="mt-8 w-full rounded-2xl bg-blue-600 py-4 text-base font-semibold text-white hover:bg-blue-500 shadow-lg shadow-blue-600/30"
          >
            Sign In with Email or Wallet
          </button>
        </section>
      </main>
    );
  }

  return (
    <main className="min-h-screen bg-slate-950 text-white">
      <section className="mx-auto max-w-7xl px-4 py-12 md:px-8 md:py-16">
        {/* Header */}
        <div className="mb-8 flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
          <div>
            <div className="mb-2 inline-block rounded-full bg-blue-500/20 px-3 py-1 text-xs font-semibold text-blue-300">
              PSE / AGPSIRA Checkout Integration
            </div>
            <h1 className="text-4xl font-bold md:text-6xl">
              AGPSIRA Checkout App
            </h1>
            <p className="mt-2 text-lg text-slate-300">
              Connect your agreement to the AGPSIRA Checkout App, generate checkout links and embed snippets, and process transactions through smart contract revenue distribution.
            </p>
          </div>

          <Link
            href="/dashboard"
            className="rounded-xl border border-slate-700 bg-slate-900 px-4 py-2 text-sm font-medium text-slate-200 hover:border-slate-500"
          >
            Back to Dashboard
          </Link>
        </div>

        <PortalNavBar links={navLinks} portalTitle="Participant Navigation" />

        {/* Top Metric Cards */}
        <div className="mb-12 grid gap-6 md:grid-cols-3">
          <div className="rounded-3xl border border-slate-800 bg-slate-900 p-8">
            <p className="mb-2 text-slate-400">Checkout Routing Status</p>
            <h2 className="text-4xl font-bold text-blue-400">Active</h2>
            <p className="mt-1 text-xs text-slate-400">
              AGPSIRA Checkout App Integration
            </p>
          </div>

          <div className="rounded-3xl border border-slate-800 bg-slate-900 p-8">
            <p className="mb-2 text-slate-400">Platform Fee Deduction</p>
            <h2 className="text-4xl font-bold text-emerald-400">1.20%</h2>
            <p className="mt-1 text-xs text-slate-400">
              Auto-distributed to AGPSIRA Platform Agreement
            </p>
          </div>

          <div className="rounded-3xl border border-slate-800 bg-slate-900 p-8">
            <p className="mb-2 text-slate-400">Automated Split</p>
            <h2 className="text-xl font-bold text-violet-400">3-Way Settlement</h2>
            <p className="mt-1 text-xs text-slate-400">
              Finix Processing + AGPSIRA Fee + Your Agreement
            </p>
          </div>
        </div>

        {/* Main Section: AGPSIRA Checkout App Route Configuration */}
        <div className="rounded-3xl border border-slate-800 bg-slate-900 p-8">
          <div className="mb-6">
            <h2 className="text-2xl font-bold">AGPSIRA Checkout App Integration</h2>
            <p className="text-sm text-slate-400 mt-1">
              Configure your agreement contract and merchant wallet to generate your direct checkout link and embed code for the AGPSIRA Checkout App.
            </p>
          </div>

          <div className="grid gap-6 lg:grid-cols-2">
            <div className="space-y-5">
              <div>
                <label className="mb-1 block text-xs font-medium text-slate-300">
                  Your Agreement Contract Address
                </label>
                <input
                  value={agreementAddress}
                  onChange={(e) => setAgreementAddress(e.target.value)}
                  placeholder="0x..."
                  className="w-full rounded-xl border border-slate-700 bg-slate-950 px-4 py-2.5 text-sm text-white outline-none focus:border-blue-500 font-mono"
                />
              </div>

              <div>
                <label className="mb-1 block text-xs font-medium text-slate-300">
                  Your Merchant / PSE Wallet Address
                </label>
                <input
                  value={customerWallet}
                  readOnly
                  placeholder="0x..."
                  className="w-full rounded-xl border border-slate-700 bg-slate-950 px-4 py-2.5 text-sm text-slate-400 outline-none font-mono"
                />
              </div>
              {creatingCheckoutLink && <p className="text-xs text-slate-400">Creating secure checkout link...</p>}
              {checkoutLinkError && <p role="alert" className="text-xs text-red-300">{checkoutLinkError}</p>}
            </div>

            {generatedLink && (
              <div className="space-y-4 rounded-2xl border border-slate-800 bg-slate-950 p-5">
                <div>
                  <div className="flex items-center justify-between mb-1.5">
                    <span className="text-xs font-semibold text-emerald-300 uppercase tracking-wider">
                      Direct Checkout App URL:
                    </span>
                    <button
                      type="button"
                      onClick={() => handleCopy(generatedLink)}
                      className="rounded-lg bg-blue-600/30 px-2.5 py-1 text-xs font-medium text-blue-200 hover:bg-blue-600/50"
                    >
                      {copied ? "Copied! ✓" : "Copy URL"}
                    </button>
                  </div>
                  <div className="rounded-xl bg-slate-900 p-3 font-mono text-xs text-blue-300 break-all border border-slate-800 select-all">
                    {generatedLink}
                  </div>
                </div>

                <div>
                  <div className="flex items-center justify-between mb-1.5">
                    <span className="text-xs font-semibold text-slate-400 uppercase tracking-wider">
                      IFrame Embed Snippet:
                    </span>
                    <button
                      type="button"
                      onClick={() => handleCopy(generatedIframe ?? "")}
                      className="rounded-lg bg-slate-800 px-2.5 py-1 text-xs font-medium text-slate-300 hover:bg-slate-700"
                    >
                      Copy Snippet
                    </button>
                  </div>
                  <textarea
                    readOnly
                    rows={3}
                    value={generatedIframe ?? ""}
                    className="w-full rounded-xl bg-slate-900 p-3 font-mono text-xs text-slate-300 border border-slate-800 select-all"
                  />
                </div>
              </div>
            )}
          </div>
        </div>

      </section>
    </main>
  );
}
